import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Rocket, Calendar, ChevronRight, CheckCircle2, Sliders, ShieldCheck
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Language } from '@/types';

interface ReleaseNotesProps {
  lang: Language;
}

interface Release {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  title: { en: string; ar: string };
  highlights: { en: string; ar: string }[];
  features?: { en: string; ar: string }[];
  improvements?: { en: string; ar: string }[];
  fixes?: { en: string; ar: string }[];
}

const ReleaseNotes: React.FC<ReleaseNotesProps> = ({ lang }) => {
  const navigate = useNavigate();
  const isRtl = lang === 'ar';

  const releases: Release[] = [
    {
      version: '2.1.0',
      date: '2026-05-23',
      type: 'major',
      title: { 
        en: 'VEXL Nova 2.1 Spatial Engine & Benchmarking Suite', 
        ar: 'محرك VEXL Nova 2.1 وحزمة المعايير المكانية'
      },
      highlights: [
        { en: 'Columnar spatial execution with DuckDB spatial and GeoParquet ingestion at 450k rows/sec', ar: 'محرك مكاني يعتمد على الأعمدة مع DuckDB وتدفق بيانات GeoParquet بسرعة 450 ألف صف/ثانية' },
        { en: 'Sub-2ms bounding-box query latency across 18.7M building footprints in Cairo metro area', ar: 'زمن استعلام أقل من 2ms لفلترة النطاقات الجغرافية عبر 18.7 مليون مبنى في القاهرة الكبرى' },
        { en: 'Unified VEXL Design System v2.0 adhering to remote sensing color conventions', ar: 'منظومة التصميم الموحدة v2.0 المستندة إلى معايير الاستشعار عن بعد' },
      ],
      features: [
        { en: 'Full-duplex spatial streaming API with WebSocket & Arrow flight endpoints', ar: 'واجهة بث مكاني ثنائية مع نقاط اتصال WebSocket و Arrow' },
        { en: 'Autonomous GeoAgent multi-modal spatial reasoning pipeline', ar: 'نظام وكلاء الذكاء الاصطناعي المكاني للاستدلال الجغرافي المستقل' },
        { en: 'Interactive Leaflet vector tile inspection layers with custom CRS projections', ar: 'طبقات فحص البلاطات المتجهة التفاعلية مع دعم أنظمة الإحداثيات المختلفة' },
      ],
      improvements: [
        { en: 'Reduced client bundle size by 62% via tree-shaking and vector compression', ar: 'تقليص حجم حزمة الواجهة بنسبة 62٪ عبر ضغط المتجهات' },
        { en: 'Zero-latency tab switching across spatial layers and analytics engines', ar: 'تنقل فوري بين طبقات التحليل ومحركات الحساب' },
        { en: 'Sub-second session persistence and memory cache optimization', ar: 'تحسين استمرارية الجلسات والذاكرة المؤقتة' },
      ],
      fixes: [
        { en: 'Resolved coordinate precision rounding issues in EPSG:4326 to EPSG:3857 transforms', ar: 'حل مشكلات تقريب دقة الإحداثيات عند التحويل بين أنظمة الإسقاط' },
        { en: 'Eliminated event-loop blocking on multi-megabyte GeoJSON parsing', ar: 'إلغاء حظر مسار المعالجة الرئيسي عند تحليل ملفات GeoJSON الضخمة' },
        { en: 'Fixed theme state synchronization in enterprise desktop bridge', ar: 'معالجة مزامنة حالة الثيم في جسر تطبيق سطح المكتب' },
      ],
    },
    {
      version: '2.0.0',
      date: '2026-03-15',
      type: 'major',
      title: { 
        en: 'VEXL Spatial Intelligence Platform Launch', 
        ar: 'إطلاق منصة VEXL للذكاء الاصطناعي المكاني'
      },
      highlights: [
        { en: 'Launch of VEXL Nova conversational AI for geospatial analytics', ar: 'إطلاق VEXL Nova للتحليل الجغرافي المحادثي' },
        { en: 'Desktop GIS integration bridge for ArcGIS Pro and QGIS', ar: 'جسر الربط مع برمجيات سطح المكتب ArcGIS Pro و QGIS' },
        { en: 'Enterprise-grade spatial database connectivity with PostGIS and DuckDB', ar: 'اتصال متقدم بقواعد البيانات المكانية عبر PostGIS و DuckDB' },
      ],
      features: [
        { en: 'Streaming token generation with real-time geospatial code compilation', ar: 'توليد متدفق للنتائج مع تنفيذ فوري لأكواد Python المكانية' },
        { en: 'Direct ingestion for Shapefiles, GeoTIFF, and STAC catalogs', ar: 'استيراد مباشر لملفات Shapefiles و GeoTIFF وفهارس STAC' },
        { en: 'Automated NDVI and moisture index calculation pipelines', ar: 'خطوط إنتاج تلقائية لحساب مؤشرات الغطاء النباتي والرطوبة' },
      ],
      improvements: [
        { en: 'Optimized serverless GPU inference latency for satellite vision models', ar: 'تسريع زمن استجابة نماذج الرؤية الفضائية على خوادم GPU' },
        { en: 'Unified dark-mode void interface architecture', ar: 'بنية واجهة المستخدم الموحدة بتصميم الفراغ الداكن' },
      ],
      fixes: [
        { en: 'Resolved token refresh race conditions during long-running spatial jobs', ar: 'معالجة تجديد التوكن أثناء المهام المكانية طويلة المدى' },
        { en: 'Fixed memory leaks on large raster tiling loops', ar: 'إصلاح تسرب الذاكرة أثناء معالجة الصور النقطية الكبيرة' },
      ],
    },
  ];

  return (
    <PageTransition>
      <SEO 
        title={lang === 'ar' ? 'سجل التحديثات والإصدارات | VEXL' : 'Release Notes & Changelog | VEXL'}
        description="Official release notes, changelog, and technical updates for VEXL platforms."
        lang={lang}
      />
      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden ${isRtl ? 'font-arabic text-right' : 'font-sans text-left'}`} 
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-28 pb-24">
          
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-xs font-mono tracking-widest uppercase mb-10"
          >
            <ArrowLeft size={14} className={isRtl ? 'rotate-180' : ''} />
            <span>{lang === 'ar' ? 'العودة للخلف' : 'Back'}</span>
          </motion.button>

          {/* Two-Column Editorial Split Hero */}
          <div className="pb-16 border-b border-[#262C31] mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
              
              {/* Left Column: Headline */}
              <div className="lg:col-span-7">
                <h1 
                  className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {lang === 'ar' ? 'سجل إصدارات وتحديثات VEXL' : 'Release Notes & Platform Changelog'}
                </h1>
              </div>

              {/* Right Column: Editorial Description & Live Indicator */}
              <div className="lg:col-span-5 space-y-4">
                <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                  {lang === 'ar'
                    ? 'سجل التحديثات المستمرة لمحركات التحليل المكاني، واجهات المستخدم، والميزات الجديدة في بيئة VEXL.'
                    : 'Chronological timeline of system enhancements, architectural upgrades, and spatial computation features across VEXL releases.'}
                </p>
                <div className="pt-2">
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] text-[#0E93A3] uppercase tracking-[0.18em]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] animate-pulse" />
                    {lang === 'ar' ? 'أحدث إصدار مستقر: v2.1.0' : 'Latest Stable Release · v2.1.0'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Releases Timeline */}
          <div className="max-w-4xl space-y-12">
            {releases.map((release, idx) => (
              <motion.div
                key={release.version}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
                className="relative"
              >
                {/* Timeline connector line */}
                {idx < releases.length - 1 && (
                  <div className="absolute top-14 left-5 w-px h-[calc(100%+2rem)] bg-[#262C31]" />
                )}

                <div className="flex items-start gap-6">
                  {/* Version Dot Marker */}
                  <div className="shrink-0 w-10 h-10 rounded-full bg-[#12161A] border border-[#262C31] flex items-center justify-center text-[#0E93A3] relative z-10">
                    <Rocket size={16} />
                  </div>

                  {/* Release Content Card */}
                  <div className="flex-1 p-6 md:p-8 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all">
                    {/* Header Strip */}
                    <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[#262C31] mb-6">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-mono font-bold text-[#EDF0F1]">v{release.version}</span>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-[#0E93A3]/10 text-[#0E93A3] border border-[#0E93A3]/20">
                          {release.type === 'major' ? (lang === 'ar' ? 'إصدار رئيسي' : 'Major Release') : (lang === 'ar' ? 'تحديث فرعي' : 'Minor Update')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-mono text-[#9AA5AC]">
                        <Calendar size={13} />
                        <span>{release.date}</span>
                      </div>
                    </div>

                    <h3 className="text-lg md:text-xl font-semibold text-[#EDF0F1] mb-6">
                      {release.title[lang]}
                    </h3>

                    {/* Highlights */}
                    <div className="mb-6">
                      <h4 className="text-xs font-mono text-[#0E93A3] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <CheckCircle2 size={13} />
                        <span>{lang === 'ar' ? 'أبرز الإنجازات' : 'Key Milestones'}</span>
                      </h4>
                      <ul className="space-y-2">
                        {release.highlights.map((highlight, hIdx) => (
                          <li key={hIdx} className="flex items-start gap-2.5 text-sm text-[#EDF0F1] leading-relaxed">
                            <ChevronRight size={14} className="shrink-0 mt-1 text-[#0E93A3]" />
                            <span>{highlight[lang]}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Features */}
                    {release.features && (
                      <div className="mb-6 pt-4 border-t border-[#262C31]">
                        <h4 className="text-xs font-mono text-[#639922] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Sliders size={13} />
                          <span>{lang === 'ar' ? 'الميزات الجديدة' : 'New Capabilities'}</span>
                        </h4>
                        <ul className="space-y-2">
                          {release.features.map((feature, fIdx) => (
                            <li key={fIdx} className="text-sm text-[#9AA5AC] flex items-start gap-2.5 leading-relaxed">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#639922] mt-2 shrink-0" />
                              <span>{feature[lang]}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Improvements & Fixes */}
                    {(release.improvements || release.fixes) && (
                      <div className="pt-4 border-t border-[#262C31] grid md:grid-cols-2 gap-6">
                        {release.improvements && (
                          <div>
                            <h4 className="text-xs font-mono text-[#9AA5AC] uppercase tracking-wider mb-2">
                              {lang === 'ar' ? 'التحسينات' : 'Performance Enhancements'}
                            </h4>
                            <ul className="space-y-1.5">
                              {release.improvements.map((imp, iIdx) => (
                                <li key={iIdx} className="text-xs text-[#9AA5AC] flex items-start gap-2">
                                  <span className="w-1 h-1 rounded-full bg-[#262C31] mt-1.5 shrink-0" />
                                  <span>{imp[lang]}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {release.fixes && (
                          <div>
                            <h4 className="text-xs font-mono text-[#9AA5AC] uppercase tracking-wider mb-2">
                              {lang === 'ar' ? 'الإصلاحات' : 'Stability Fixes'}
                            </h4>
                            <ul className="space-y-1.5">
                              {release.fixes.map((fix, fxIdx) => (
                                <li key={fxIdx} className="text-xs text-[#9AA5AC] flex items-start gap-2">
                                  <span className="w-1 h-1 rounded-full bg-[#262C31] mt-1.5 shrink-0" />
                                  <span>{fix[lang]}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default ReleaseNotes;
